/**
 * CHARACTER CAPS FOR AN OPAQUE EMAIL. 2026-08-31.
 *
 * Jack, on FNO pilot feedback: "Should we allow resends for templates we don't have
 * locked down? There were no character limits and I could change icons."
 *
 * A resend hands the Studio a finished email with no block types on it — the frame
 * editor knows only what a section LOOKS like. So the declared caps have to be
 * projected onto those shape-based labels.
 *
 * TWO VOCABULARIES, ONE RULESET. The block registry names things the way Layne named
 * them in Marketing Cloud ("CTA Button", "Amenities (4 Across)"); `labelFor` in
 * TemplateEditor classifies an opaque email by shape and says "BUTTON / CTA",
 * "AMENITY ICONS". Only "BODY COPY" happens to coincide.
 *
 * ⚠ THE ALIAS MAP IS MEASURED, NOT INHERITED. The monolith carried its own alias map,
 * and porting it verbatim would have matched almost nothing here: five of its twelve
 * keys point at registry labels that do not exist in this build — "GET DIRECTIONS (WITH
 * VISIT US BAR)", "COMMUNITY SUBHEAD", "JUST ANNOUNCED / COMING SOON", "ISC CONTACT",
 * "DISCLAIMER + TITLE LOGO". The real labels, read out of the registry, are "Get
 * Directions", "Section Bar (No Link)", "Coming Soon Banner", "Contact Us",
 * "Disclaimer". A silent no-match is the worst outcome here — it looks like the caps
 * are on while nothing is enforced — so `unmappedFrameLabels()` exists to keep this
 * honest and a test asserts every shape is either mapped or listed as uncapped.
 *
 * WIDEST CAP ON THE BLOCK WINS. The point is to stop runaway copy that breaks the
 * artwork, not to guess which of a block's fields a given line came from.
 */
import { echTypes, echMeta } from "./echRegistry";
import { FULL_REGISTRY } from "../email/blocks";
import { capOf } from "./guardrails";

/** every label `labelFor` (TemplateEditor) can return */
export const FRAME_LABELS = [
  "FOOTER", "SOCIAL FOOTER", "LENNAR MORTGAGE", "EVERYTHING'S INCLUDED", "MAP / ADDRESS",
  "WE'RE HERE TO HELP", "WHY BUY LENNAR", "OFFER / SALES MESSAGE", "HOMESITE LISTING",
  "FEATURED FLOORPLAN", "HERO IMAGE", "LOGO HEADER", "AMENITY ICONS", "IMAGE", "SPACER",
  "BUTTON / CTA", "EYEBROW", "HEADLINE", "BODY COPY",
] as const;

/**
 * frame label → the registry block whose declared limit governs it.
 * Keys are frame labels; values are registry labels exactly as `FULL_REGISTRY[t].label`
 * spells them. Verified against the live registry 2026-08-31.
 */
const ALIAS: Record<string, string> = {
  "BUTTON / CTA": "CTA Button",                 /* 20 */
  "AMENITY ICONS": "Amenities (4 Across)",      /* 16 */
  "HOMESITE LISTING": "Community Home Table",   /* 60 — the block whose rows carry
                                                   "sq ft / beds / baths", which is what
                                                   this shape detects. NOT "Listing
                                                   Table" (34), a different block. */
  "FEATURED FLOORPLAN": "Featured Plan",        /* 100 */
  "MAP / ADDRESS": "Get Directions",            /* 120 */
  "HERO IMAGE": "Main Image",                   /* 100 */
  "IMAGE": "Main Image",                        /* 100 */
  "HEADLINE": "Header",                         /* 50 */
  "EYEBROW": "Section Bar (No Link)",           /* 48 */
  "BODY COPY": "Body Copy",                     /* 325 — the one exact name match */
};

/**
 * Shapes deliberately left UNCAPPED, with the reason. Listed rather than omitted so
 * `unmappedFrameLabels()` can tell "considered and declined" from "forgotten".
 */
export const UNCAPPED: Record<string, string> = {
  "LOGO HEADER": "artwork, no requester copy to cap",
  "SPACER": "no text at all",
  "FOOTER": "Disclaimer declares no enforceable cap (legal copy is not length-governed)",
  "SOCIAL FOOTER": "Social Icons Bar declares no enforceable cap",
  "WE'RE HERE TO HELP": "Contact Us declares no enforceable cap",
  "OFFER / SALES MESSAGE": "Coming Soon Banner declares no enforceable cap",
  "LENNAR MORTGAGE": "no ECH block governs it — mortgage copy is supplied, not typed here",
  "EVERYTHING'S INCLUDED": "no ECH block governs it",
  "WHY BUY LENNAR": "no ECH block governs it",
};

/** registry label (upper-cased) → widest declared cap on that block */
function widestByRegistryLabel(): Record<string, number> {
  const out: Record<string, number> = {};
  for (const t of echTypes()) {
    const m = echMeta(t);
    const fields = m?.gr?.fields;
    if (!fields) continue;
    const lab = String((FULL_REGISTRY[t] as { label?: string } | undefined)?.label || m?.sfmc.name || t).toUpperCase();
    if (!lab) continue;
    let wide = 0;
    for (const rule of Object.values(fields)) {
      const c = capOf(rule);
      if (c > wide) wide = c;
    }
    if (wide && (!out[lab] || wide > out[lab])) out[lab] = wide;
  }
  return out;
}

/**
 * frame label → cap. Only labels with a real, non-zero cap appear; the editor treats a
 * missing entry as "no limit", which is the same thing it did before this existed.
 */
export function capsByLabel(): Record<string, number> {
  const byReg = widestByRegistryLabel();
  const out: Record<string, number> = {};
  for (const [frame, reg] of Object.entries(ALIAS)) {
    const cap = byReg[reg.toUpperCase()];
    if (cap) out[frame] = cap;
  }
  return out;
}

/**
 * The honesty check: frame labels that are neither capped nor explicitly declined.
 * A non-empty result means a shape silently escapes the caps — which is how this whole
 * mechanism fails quietly. Asserted by a test; also safe to call at runtime.
 */
export function unmappedFrameLabels(): string[] {
  const caps = capsByLabel();
  return FRAME_LABELS.filter((l) => !caps[l] && !UNCAPPED[l]);
}
